import FilesService from '@/modules/files/services/filesService';
import { LEAD_INDICATION_DOWNLOAD_ERROR_MESSAGE } from '@/modules/leadIndication/entities/constants';
import {
  LeadIndication,
  LeadIndicationError,
  mapLeadIndicationError,
} from '@/modules/leadIndication/entities/leadIndication';
import useCompany from '@/shared/hooks/useCompany/useCompany';
import { dispatchToast } from '@/utils/tryOrCatchMessageError';
import { useCallback, useState } from 'react';

const filesService = new FilesService();

interface UseLeadIndicationDownloadOptions {
  onSuccess?: (indication: LeadIndication) => void;
  onError?: (error: LeadIndicationError, indication: LeadIndication) => void;
}

const useLeadIndicationDownload = ({
  onSuccess,
  onError,
}: UseLeadIndicationDownloadOptions = {}) => {
  const { company } = useCompany();
  const [loadingFileId, setLoadingFileId] = useState<string | null>(null);
  const [error, setError] = useState<LeadIndicationError | null>(null);

  const download = useCallback(
    async (indication: LeadIndication) => {
      const companyId = indication.fileCompanyId || company.id;

      if (!indication.fileId || !companyId) {
        return false;
      }

      setLoadingFileId(indication.fileId);
      setError(null);

      try {
        const response = await filesService.getDownloadToken(companyId, indication.fileId);
        window.open(response.link, '_blank');
        onSuccess?.(indication);

        return true;
      } catch (downloadError) {
        const normalizedError = mapLeadIndicationError(
          downloadError,
          LEAD_INDICATION_DOWNLOAD_ERROR_MESSAGE,
        );

        setError(normalizedError);
        dispatchToast({ text: LEAD_INDICATION_DOWNLOAD_ERROR_MESSAGE });
        onError?.(normalizedError, indication);

        return false;
      } finally {
        setLoadingFileId(null);
      }
    },
    [company.id, onError, onSuccess],
  );

  return {
    download,
    loadingFileId,
    error,
  };
};

export default useLeadIndicationDownload;
